import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import InfoTag from '../InfoTag/InfoTag';
import RichTextArea from '../../../../design-system/RichTextArea/RichTextArea';
import ErrorMessage from '../../../../design-system/ErrorMessage/ErrorMessage';

const StepItem = ({ step, setGroupFormData, isEditable }) => {
    const { order, name, description, details, settings, context, state } = step;

    const [isExpanded, setIsExpanded] = useState(false);
    const buttonText = isExpanded ? 'Hide Details' : 'View Details';

    const toggleExpand = () => setIsExpanded((prev) => !prev);

    useEffect(() => {
        if (state === 'error' && !isExpanded) {
            toggleExpand();
        }
    }, [state]);

    const setStep = (cb) => {
        setGroupFormData((steps) => {
            return steps.map((step) => {
                if (step.name === name && step.order === order) {
                    const updatedStep = typeof cb === 'function' ? cb(step) : cb;
                    return { ...updatedStep, state: 'default' };
                }
                return step;
            });
        });
    };

    const withTags = !!settings?.length || !!context?.length;

    const containerClassName = classNames(
        'w-full p-4 flex flex-col gap-4 rounded-2 border border-neutral-200 transition bg-white',
        {
            'border-neutral-200': state !== 'error',
            'border-red-500': state === 'error',
        }
    );

    return (
        <li key={name + order} className="flex flex-col gap-1">
            <div className={containerClassName}>
                <div className="flex items-center gap-2">
                    <div className="min-w-[20px] h-[20px] rounded-full bg-purple-100 font-body text-body-regular-xs text-purple-500 flex items-center justify-center">
                        {order}
                    </div>
                    <p className="font-body-bold text-body-bold-m text-neutral-500">{name}</p>
                </div>

                <RichTextArea value={description} previewOnly autoExpand isBorderHidden />

                <button
                    onClick={toggleExpand}
                    className="block w-fit font-body text-body-regular-s text-purple-500 underline"
                >
                    {buttonText}
                </button>

                {isExpanded && (
                    <>
                        <RichTextArea value={details} previewOnly autoExpand isBorderHidden />

                        {withTags && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                {settings?.map((setting) => {
                                    const setSetting = (cb) => {
                                        setStep((step) => {
                                            const settings = step.settings.map((item) => {
                                                if (item.name === setting.name) {
                                                    return typeof cb === 'function' ? cb(item) : cb;
                                                }
                                                return item;
                                            });
                                            return { ...step, settings };
                                        });
                                    };

                                    return (
                                        <InfoTag
                                            key={setting.name}
                                            type="setting"
                                            data={setting}
                                            setData={setSetting}
                                            isEditable={isEditable}
                                        />
                                    );
                                })}

                                {context?.map((context) => {
                                    const setContext = (cb) => {
                                        setStep((step) => {
                                            const formattedContext = step.context.map((item) => {
                                                if (item.name === context.name) {
                                                    return typeof cb === 'function' ? cb(item) : cb;
                                                }
                                                return item;
                                            });
                                            return {
                                                ...step,
                                                context: formattedContext,
                                            };
                                        });
                                    };

                                    return (
                                        <InfoTag
                                            key={context.name}
                                            type="context"
                                            data={context}
                                            setData={setContext}
                                            isEditable={isEditable}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {state === 'error' && <ErrorMessage message="Please fill in all tags with errors." />}
        </li>
    );
};

export default StepItem;
